/** 프로그램·연간진도표 공통: 통합 저장 전 learning_text 편집 상태(인디케이터용) */
export const LEARNING_ATTACHMENT_INDICATOR_STORAGE_PREFIX = 'cp:learning-attachment-indicator:v1:';
const LEARNING_ATTACHMENT_INDICATOR_DELETE_PREFIX = 'cp:learning-attachment-indicator-delete:v1:';
export const LEARNING_ATTACHMENT_INDICATOR_CHANGED_EVENT = 'cp:learning-attachment-indicator-changed';

type LearningAttachmentIndicatorStoreV1 = {
  v: 1;
  byAnnualKey: Record<string, { learningText: string; updatedAt: number }>;
};

type LearningAttachmentIndicatorDeleteStoreV1 = {
  v: 1;
  byAnnualKey: Record<string, { presentation?: 1; worksheet?: 1; updatedAt: number }>;
};

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function storageKey(workspaceId: string): string {
  return `${LEARNING_ATTACHMENT_INDICATOR_STORAGE_PREFIX}${workspaceId.trim()}`;
}

function deleteStorageKey(workspaceId: string): string {
  return `${LEARNING_ATTACHMENT_INDICATOR_DELETE_PREFIX}${workspaceId.trim()}`;
}

function readStore(workspaceId: string): LearningAttachmentIndicatorStoreV1 {
  const sid = workspaceId.trim();
  if (!sid) return { v: 1, byAnnualKey: {} };
  try {
    const raw = storage()?.getItem(storageKey(sid));
    if (!raw) return { v: 1, byAnnualKey: {} };
    const parsed = JSON.parse(raw) as LearningAttachmentIndicatorStoreV1;
    if (parsed?.v !== 1 || !parsed.byAnnualKey || typeof parsed.byAnnualKey !== 'object') {
      return { v: 1, byAnnualKey: {} };
    }
    return parsed;
  } catch {
    return { v: 1, byAnnualKey: {} };
  }
}

function writeStore(workspaceId: string, store: LearningAttachmentIndicatorStoreV1): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  try {
    storage()?.setItem(storageKey(sid), JSON.stringify(store));
  } catch {
    /* ignore */
  }
}

function readDeleteStore(workspaceId: string): LearningAttachmentIndicatorDeleteStoreV1 {
  const sid = workspaceId.trim();
  if (!sid) return { v: 1, byAnnualKey: {} };
  try {
    const raw = storage()?.getItem(deleteStorageKey(sid));
    if (!raw) return { v: 1, byAnnualKey: {} };
    const parsed = JSON.parse(raw) as LearningAttachmentIndicatorDeleteStoreV1;
    if (parsed?.v !== 1 || !parsed.byAnnualKey || typeof parsed.byAnnualKey !== 'object') {
      return { v: 1, byAnnualKey: {} };
    }
    return parsed;
  } catch {
    return { v: 1, byAnnualKey: {} };
  }
}

function writeDeleteStore(workspaceId: string, store: LearningAttachmentIndicatorDeleteStoreV1): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  try {
    storage()?.setItem(deleteStorageKey(sid), JSON.stringify(store));
  } catch {
    /* ignore */
  }
}

function notifyChanged(): void {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(LEARNING_ATTACHMENT_INDICATOR_CHANGED_EVENT));
    }
  } catch {
    /* ignore */
  }
}

export function readLearningAttachmentIndicatorMap(workspaceId: string): Record<string, string> {
  const store = readStore(workspaceId);
  const out: Record<string, string> = {};
  for (const [annualKey, entry] of Object.entries(store.byAnnualKey)) {
    if (typeof entry?.learningText === 'string') out[annualKey] = entry.learningText;
  }
  return out;
}

export function readLearningAttachmentIndicatorLearningText(
  workspaceId: string,
  annualKey: string
): string | null {
  const key = annualKey.trim();
  if (!key) return null;
  const hit = readStore(workspaceId).byAnnualKey[key];
  return typeof hit?.learningText === 'string' ? hit.learningText : null;
}

export function persistLearningAttachmentIndicatorLearningText(
  workspaceId: string,
  annualKey: string,
  learningText: string
): void {
  const sid = workspaceId.trim();
  const key = annualKey.trim();
  if (!sid || !key) return;
  const store = readStore(sid);
  const prev = store.byAnnualKey[key]?.learningText;
  if (prev === learningText) return;
  store.byAnnualKey[key] = { learningText, updatedAt: Date.now() };
  writeStore(sid, store);
  notifyChanged();
}

export function removeLearningAttachmentIndicatorEntry(workspaceId: string, annualKey: string): void {
  const sid = workspaceId.trim();
  const key = annualKey.trim();
  if (!sid || !key) return;
  const store = readStore(sid);
  if (!store.byAnnualKey[key]) return;
  delete store.byAnnualKey[key];
  writeStore(sid, store);
  notifyChanged();
}

export function clearLearningAttachmentIndicatorWorkspace(workspaceId: string): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  writeStore(sid, { v: 1, byAnnualKey: {} });
  writeDeleteStore(sid, { v: 1, byAnnualKey: {} });
  notifyChanged();
}

export function clearLearningAttachmentIndicatorEntriesForAnnualKeys(
  workspaceId: string,
  annualKeys: string[]
): void {
  const sid = workspaceId.trim();
  if (!sid || !annualKeys.length) return;
  const store = readStore(sid);
  let changed = false;
  for (const annualKey of annualKeys) {
    const key = annualKey.trim();
    if (!key || !store.byAnnualKey[key]) continue;
    delete store.byAnnualKey[key];
    changed = true;
  }
  if (changed) writeStore(sid, store);
  const deleteStore = readDeleteStore(sid);
  let deleteChanged = false;
  for (const annualKey of annualKeys) {
    const key = annualKey.trim();
    if (!key || !deleteStore.byAnnualKey[key]) continue;
    delete deleteStore.byAnnualKey[key];
    deleteChanged = true;
  }
  if (deleteChanged) writeDeleteStore(sid, deleteStore);
  if (!changed && !deleteChanged) return;
  notifyChanged();
}

export function isLearningAttachmentIndicatorDeletedForKeys(
  hints: Record<string, { presentation?: boolean; worksheet?: boolean }>,
  annualKey: string,
  patchAnnualKey: string,
  kind: 'presentation' | 'worksheet'
): boolean {
  const key = annualKey.trim();
  const patchKey = patchAnnualKey.trim();
  if (key && hints[key]?.[kind]) return true;
  if (patchKey && patchKey !== key && hints[patchKey]?.[kind]) return true;
  return false;
}

export function readLearningAttachmentIndicatorDeletedHints(
  workspaceId: string
): Record<string, { presentation?: boolean; worksheet?: boolean }> {
  const store = readDeleteStore(workspaceId);
  const out: Record<string, { presentation?: boolean; worksheet?: boolean }> = {};
  for (const [annualKey, entry] of Object.entries(store.byAnnualKey)) {
    const presentation = entry?.presentation === 1;
    const worksheet = entry?.worksheet === 1;
    if (presentation || worksheet) out[annualKey] = { ...(presentation ? { presentation: true } : {}), ...(worksheet ? { worksheet: true } : {}) };
  }
  return out;
}

export function markLearningAttachmentIndicatorDeleted(
  workspaceId: string,
  annualKey: string,
  kind: 'presentation' | 'worksheet'
): void {
  const sid = workspaceId.trim();
  const key = annualKey.trim();
  if (!sid || !key) return;
  const store = readDeleteStore(sid);
  const prev = store.byAnnualKey[key] ?? { updatedAt: Date.now() };
  const before = prev[kind] === 1;
  if (before) return;
  store.byAnnualKey[key] = {
    ...prev,
    [kind]: 1,
    updatedAt: Date.now(),
  };
  writeDeleteStore(sid, store);
  notifyChanged();
}

/** 대표·미러 annualCellKey 쌍에 동일 삭제 힌트를 기록한다 */
export function markLearningAttachmentIndicatorDeletedForSyncKeys(
  workspaceId: string,
  annualKeys: string[],
  kind: 'presentation' | 'worksheet'
): void {
  const seen = new Set<string>();
  for (const annualKey of annualKeys) {
    const key = annualKey.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    markLearningAttachmentIndicatorDeleted(workspaceId, key, kind);
  }
}

export function clearLearningAttachmentIndicatorDeleted(
  workspaceId: string,
  annualKey: string,
  kind: 'presentation' | 'worksheet'
): void {
  const sid = workspaceId.trim();
  const key = annualKey.trim();
  if (!sid || !key) return;
  const store = readDeleteStore(sid);
  const prev = store.byAnnualKey[key];
  if (!prev || prev[kind] !== 1) return;
  const next = { ...prev };
  delete next[kind];
  if (!next.presentation && !next.worksheet) {
    delete store.byAnnualKey[key];
  } else {
    store.byAnnualKey[key] = { ...next, updatedAt: Date.now() };
  }
  writeDeleteStore(sid, store);
  notifyChanged();
}
